import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useParams } from "react-router-dom";
import HeaderNavbar from "./HeaderNavbar";
import Footer from "./Footer";
import LeadCaptureModal from "./LeadCaptureModal";
import { NotificationProvider } from "../../hooks/use-notification";

// Image assets from Figma
const imgHeroSection =
  "http://localhost:3845/assets/2302c8d6572c1215d46c5229925e467ed3c73847.png";
const imgHeroSection1 = "/case-study-1.svg";
const imgImage11 = "/shanti-sarovar-img.svg";
const imgIcon = "/call-Icon.svg";

const CaseStudyDetail = ({ caseStudy = {} }) => {
  const { id } = useParams();
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Default content structure
  const defaultContent = {
    title: "This is a successfully deployed case study",
    subtitle:
      "validating the real-world application of the MyBovine.ai ecosystem in a live environment.",
    location: "Shanti Sarovar, Hyderabad",
    heroImage1: imgHeroSection,
    heroImage2: imgHeroSection1,
    sections: [
      {
        id: "problem",
        title: "1. The Problem: The Latency of Manual Observation",
        content: `At Shanti Sarovar, the primary bottleneck was the detection-to-intervention lag. Traditional husbandry relies on visual markers (lethargy, decreased milk let-down), which are "lagging indicators." By the time these are visible, the animal's metabolic state has already deteriorated.`,
        subsections: [
          {
            title: "Empirical Challenges:",
            items: [
              {
                bold: "Thermal Invisibility:",
                text: " Clinical fevers often precede physical symptoms by 24–72 hours.",
              },
              {
                bold: "Sub-clinical Acidosis:",
                text: " Identifying rumination drops before they manifest as acute digestive distress.",
              },
              {
                bold: "Labor Asymmetry:",
                text: " 40 man-hours/week were dedicated to passive observation with zero data-capture utility.",
              },
            ],
          },
        ],
      },
      {
        id: "solution",
        title: "2. Technical Solution:",
        content:
          "We deployed a decentralized IoT architecture consisting of 9 Vanix® Wearable Nodes and a Gateway. The system transitioned the farm from subjective observation to Empirical Bio-Monitoring.",
        subsections: [
          {
            title: "The Analytical Framework:",
            items: [
              {
                bold: "Dynamic Baselining:",
                text: ' Every cow\'s "normal" is calculated based on a rolling 7-day average of activity and thermal data.',
              },
              {
                bold: "Anomaly Detection:",
                text: " The system flags deviations, ensuring high sensitivity to early-stage infection.",
              },
              {
                bold: "Multi-Modal Data Fusion:",
                text: " Correlating a spike in Temperature (TEMP) with a simultaneous drop in Rumination (RUM) to confirm systemic illness versus environmental heat stress.",
              },
            ],
          },
        ],
        image: imgImage11,
      },
      {
        id: "results",
        title: "3. Empirical Results & KPI Impact",
        content:
          "Over a 120-day observation period, the deployment yielded statistically significant improvements in farm efficiency and animal survivability.",
        table: {
          headers: [
            "Metric",
            "Pre-Deployment (Manual)",
            "Post-Deployment (MyBovine.AI)",
          ],
          rows: [
            ["Illness Rate", "60%", "30%"],
            ["Heat Detection Accuracy (HDR)", "50%", "80%"],
            ["Monitoring Labor (Hours/Week)", "40", "20"],
            ["Critical Asset Survival", "N/A", "N/A"],
          ],
        },
        keyFindings: [
          {
            bold: "Early Intervention Window:",
            text: " The system identified thermal anomalies an average of 48.5 hours before the farm manager could visually identify distress.",
          },
          {
            bold: "Reproductive Efficiency:",
            text: ' By identifying "Estrus Spikes" (hyper-activity), the farm optimized the Artificial Insemination (AI) window, reducing the calving interval.',
          },
          {
            bold: "Labor Optimization:",
            text: ' Automated alerts replaced the "watch and wait" protocol, allowing staff to reallocate 50% of their time to high-value caregiving tasks.',
          },
        ],
      },
      {
        id: "conclusion",
        title: "4. Conclusion",
        content:
          "The Shanti Sarovar deployment proves that predictive bio-analytics are viable and essential even for small-scale, high-care environments. By converting raw biological signals into Empirical Data Streams, we successfully protected the farm's capital assets (the cattle) and stabilized the milk production cycle.",
      },
    ],
  };

  const content = { ...defaultContent, ...caseStudy };

  return (
    <NotificationProvider>
      <div className="w-full bg-white" style={{ fontFamily: "Noto Sans, sans-serif" }}>
        {/* Header */}
        <HeaderNavbar onOpenModal={() => setIsModalOpen(true)} />

        {/* Lead Capture Modal */}
        <LeadCaptureModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
        />

        {/* Main Content */}
        <main
          className="w-full py-8 md:py-16 px-4 sm:px-6 md:px-12 lg:px-16"
          style={{ maxWidth: "1440px", margin: "0 auto" }}
        >
          {/* Hero Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: false }}
            className="w-full mt-6 md:mt-10 mb-10 md:mb-20 relative rounded-2xl overflow-hidden"
          >
            <div className="relative min-h-[280px] sm:min-h-[320px] md:h-96 rounded-2xl overflow-hidden">
              {/* Background Images */}
              <img
                src={content.heroImage1}
                alt="Hero background"
                className="absolute inset-0 w-full h-full object-cover"
              />
              <img
                src={content.heroImage2}
                alt="Hero background overlay"
                className="absolute inset-0 w-full h-full object-cover"
              />

              {/* Gradient Overlay */}
              <div className="absolute inset-0 bg-gradient-to-r from-[rgba(20,27,44,0.8)] to-[rgba(66,90,146,0.48)]" />

              {/* Content */}
              <div className="relative h-full flex flex-col justify-center p-6 sm:p-10 md:p-16 max-w-2xl">
                <h1 className="text-2xl sm:text-3xl md:text-5xl font-bold text-white leading-tight mb-4 md:mb-6">
                  {content.title}
                </h1>
                <p className="text-base sm:text-lg md:text-2xl text-gray-200 mb-4 md:mb-6 leading-relaxed">
                  {content.subtitle}
                </p>
                <p className="text-base sm:text-lg md:text-2xl font-bold text-white">
                  {content.location}
                </p>
              </div>
            </div>
          </motion.div>

          <div className="flex flex-col lg:flex-row gap-8 lg:gap-16">
            {/* Main Content */}
            <div className="flex-1 min-w-0">
              {content.sections.map((section, idx) => (
                <motion.div
                  key={section.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: false }}
                  className="mb-8 md:mb-12 bg-gray-100 rounded-xl p-4 sm:p-6 md:p-8"
                >
                  <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4 md:mb-6">
                    {section.title}
                  </h2>

                  {/* Main Content */}
                  <p className="text-base md:text-lg text-gray-700 leading-relaxed mb-4 md:mb-6">
                    {section.content}
                  </p>

                  {/* Subsections */}
                  {section.subsections && section.subsections.length > 0 && (
                    <div className="space-y-6 mb-6">
                      {section.subsections.map((subsection, subIdx) => (
                        <div key={subIdx}>
                          <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-3 md:mb-4">
                            {subsection.title}
                          </h3>
                          <ul className="space-y-3 list-disc list-inside">
                            {subsection.items.map((item, itemIdx) => (
                              <li
                                key={itemIdx}
                                className="text-gray-700 text-base md:text-lg"
                              >
                                <span className="font-bold">{item.bold}</span>
                                {item.text}
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Image */}
                  {section.image && (
                    <div className="mb-6 rounded-xl overflow-hidden">
                      <img
                        src={section.image}
                        alt={section.title}
                        className="w-full h-auto object-cover rounded-xl"
                      />
                    </div>
                  )}

                  {/* Table */}
                  {section.table && (
                    <div className="mb-6 overflow-x-auto -mx-4 sm:mx-0 px-4 sm:px-0">
                      <table className="w-full border-collapse min-w-[480px]">
                        <thead>
                          <tr>
                            {section.table.headers.map((header, headerIdx) => (
                              <th
                                key={headerIdx}
                                className="bg-white border border-gray-300 px-3 md:px-4 py-3 md:py-4 text-left font-bold text-gray-900 text-sm md:text-base"
                              >
                                {header}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {section.table.rows.map((row, rowIdx) => (
                            <tr key={rowIdx}>
                              {row.map((cell, cellIdx) => (
                                <td
                                  key={cellIdx}
                                  className="bg-gray-50 border border-gray-300 px-3 md:px-4 py-3 md:py-4 text-gray-700 text-center text-sm md:text-base"
                                >
                                  {cell}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {/* Key Findings */}
                  {section.keyFindings && (
                    <div className="space-y-4">
                      <h3 className="text-xl font-bold text-gray-900">
                        Key Findings:
                      </h3>
                      <ul className="space-y-3 list-disc list-inside">
                        {section.keyFindings.map((finding, findingIdx) => (
                          <li
                            key={findingIdx}
                            className="text-gray-700 text-base md:text-lg"
                          >
                            <span className="font-bold">{finding.bold}</span>
                            {finding.text}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </motion.div>
              ))}
            </div>

            {/* Sidebar CTA */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: false }}
              className="w-full lg:w-80 lg:sticky lg:top-24 shrink-0"
            >
              <div
                className="rounded-2xl p-6 text-white"
                style={{
                  background:
                    "linear-gradient(128.5deg, rgb(52, 204, 128) 2%, rgb(0, 132, 209) 100%)",
                }}
              >
                <div className="mb-4">
                  <img src={imgIcon} alt="Icon" className="w-8 h-8" />
                </div>
                <h3 className="text-xl font-bold mb-3">
                  Ready to Modernise Your Farm?
                </h3>
                <p className="text-sm text-white/80 mb-6">
                  We are available 24/7 to assist with your herd management
                  deployment.
                </p>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setIsModalOpen(true)}
                  className="w-full bg-white text-[#00bd7e] font-bold py-3 rounded-xl hover:bg-gray-100 transition-colors"
                >
                  Schedule a Call
                </motion.button>
              </div>
            </motion.div>
          </div>
        </main>

        {/* Footer */}
        <Footer />
      </div>
    </NotificationProvider>
  );
};

export default CaseStudyDetail;
